import { Box } from "@mui/material";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { IconCreditCard, IconInfoCircle } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import Text from "../../../../components/ui/Text";

export const CustomStripeForm = ({
  onCardReady,
  onPaymentProcess,
  onFormValidityChange,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const hasNotifiedParent = useRef(false);

  const [errors, setErrors] = useState({
    cardNumber: null,
    expiry: null,
    cvc: null,
  });

  const [completeness, setCompleteness] = useState({
    cardNumber: false,
    expiry: false,
    cvc: false,
  });

  // Notify parent when form is ready and provide card element access
  useEffect(() => {
    if (stripe && elements && onCardReady && !hasNotifiedParent.current) {
      onCardReady({ stripe, elements });
      hasNotifiedParent.current = true;
    }
  }, [stripe, elements, onCardReady]);

  // Handle payment processing when requested by parent
  useEffect(() => {
    if (onPaymentProcess && stripe && elements) {
      const cardElement = elements.getElement(CardNumberElement);
      if (cardElement) {
        onPaymentProcess({ stripe, elements, cardElement });
      }
    }
  }, [onPaymentProcess, stripe, elements]);

  // Check form validity and notify parent
  useEffect(() => {
    if (onFormValidityChange) {
      const hasErrors = errors.cardNumber || errors.expiry || errors.cvc;
      const isComplete =
        completeness.cardNumber && completeness.expiry && completeness.cvc;
      const isValid = !hasErrors && isComplete;
      onFormValidityChange(isValid);
    }
  }, [errors, completeness, onFormValidityChange]);

  // Custom style for Stripe elements to match the design
  const ELEMENT_OPTIONS = {
    style: {
      base: {
        fontSize: "16px",
        color: "#000",
        fontFamily: "inherit",
        fontWeight: "normal",
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: "#999",
        },
        ":-webkit-autofill": {
          color: "#000",
        },
      },
      invalid: {
        color: "#000",
        iconColor: "#000",
      },
      complete: {
        color: "#000",
      },
    },
  };

  const handleCardNumberChange = (event) => {
    setErrors((prev) => ({
      ...prev,
      cardNumber: event.error ? event.error.message : null,
    }));
    setCompleteness((prev) => ({
      ...prev,
      cardNumber: event.complete,
    }));
  };
  const handleExpiryChange = (event) => {
    setErrors((prev) => ({
      ...prev,
      expiry: event.error ? event.error.message : null,
    }));
    setCompleteness((prev) => ({
      ...prev,
      expiry: event.complete,
    }));
  };
  const handleCvcChange = (event) => {
    setErrors((prev) => ({
      ...prev,
      cvc: event.error ? event.error.message : null,
    }));
    setCompleteness((prev) => ({
      ...prev,
      cvc: event.complete,
    }));
  };

  return (
    <Box>
      {/* Card Number Field */}
      <Box sx={{ mb: 2 }}>
        <Text variant="paragraph3" sx={{ fontWeight: 500 }}>
          Card number
        </Text>
        <Box
          sx={{
            border: errors.cardNumber
              ? "1px solid #FB2C36"
              : "1px solid #E5E5E5",
            borderRadius: "8px",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "white",
            mt: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <CardNumberElement
              options={ELEMENT_OPTIONS}
              onChange={handleCardNumberChange}
            />
          </Box>
          <IconCreditCard size={20} color="#666" />
        </Box>
        {errors.cardNumber && (
          <Text variant="paragraph3" sx={{ color: "#FB2C36", mt: 0.5 }}>
            {errors.cardNumber}
          </Text>
        )}
      </Box>

      {/* Expiry and CVC Row */}
      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Expiry Date */}
        <Box sx={{ flex: 1 }}>
          <Text variant="paragraph3" sx={{ fontWeight: 500 }}>
            Expiry date
          </Text>
          <Box
            sx={{
              border: errors.expiry ? "1px solid #FB2C36" : "1px solid #E5E5E5",
              borderRadius: "8px",
              px: 2,
              py: 1,
              backgroundColor: "white",
              mt: 1,
            }}
          >
            <CardExpiryElement
              options={ELEMENT_OPTIONS}
              onChange={handleExpiryChange}
            />
          </Box>
          {errors.expiry && (
            <Text variant="paragraph3" sx={{ color: "#FB2C36", mt: 0.5 }}>
              {errors.expiry}
            </Text>
          )}
        </Box>

        {/* CVV */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Text variant="paragraph3" sx={{ fontWeight: 500 }}>
              CVC
            </Text>
            <IconInfoCircle size={16} color="#666" />
          </Box>
          <Box
            sx={{
              border: errors.cvc ? "1px solid #FB2C36" : "1px solid #E5E5E5",
              borderRadius: "8px",
              px: 2,
              py: 1,
              mt: 1,
              backgroundColor: "white",
            }}
          >
            <CardCvcElement
              options={ELEMENT_OPTIONS}
              onChange={handleCvcChange}
            />
          </Box>
          {errors.cvc && (
            <Text variant="paragraph3" sx={{ color: "#FB2C36", mt: 0.5 }}>
              {errors.cvc}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
};
